import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { chevronBackOutline } from 'ionicons/icons'
import {
  addPhysicianData,
  clearPhysicianData,
  savePatientDemographics,
  setPatientDemographicsDefaults,
  setPhysicianFirstName,
  setPhysicianLastName,
  setPhysicianMiddleName,
} from '../store/patientDemographics.actions'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PatientDemographicsContent from '../patientDemographicsContent/PatientDemographicsContent.component'
import './PatientDemographics.page.css'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { getZipSelector } from '../../../components/zip/selectors/zip.selector'
import { hasUnsavedPhysiciansData, showUnsavedDataWarningMessage } from '../../../utilities/unSavedData.util'
import { getPatientDemographicsSelector } from '../selectors/getPatientDemographics.selector'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { PatientDemographicsConstants } from '../store/patientDemographics.constants'
import { getRulesSelector } from '../../rules/selectors/getRules.selector'
import { IRuleError, IRulesState } from '../../rules/store/rules.types'

const PatientDemographics: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const zip = useSelector(getZipSelector)
  const { alertMessage } = zip
  const rules: IRulesState = useSelector(getRulesSelector)
  const { errors } = rules

  const history = useHistory()
  const patientDemographics = useSelector(getPatientDemographicsSelector)

  const onBackClickYes = () => {
    dispatch(clearPhysicianData())
    rules.errors = clearValidationErrorsByPathList(rules.errors, [
      PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME,
      'state_patientDemographics/SET_PHYSICIAN_LASTNAME',
      'state_patientDemographics/SET_PHYSICIAN_FIRSTNAME',
      'state_patientDemographics/SET_PHYSICIAN_MIDDLENAME',
    ])
    history.push(TabConstants.PATIENT_INFO_ROUTE)
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (hasUnsavedPhysiciansData(patientDemographics)) {
      showUnsavedDataWarningMessage(dispatch, t, t('patientDemographics.unsavedDataWarningMessage'), onBackClickYes)
      return
    }
    onBackClickYes()
  }

  const saveOnClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()

    const reduxActions: string[] = [
      PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME,
      'state_patientDemographics/SET_PHYSICIAN_LASTNAME',
      'state_patientDemographics/SET_PHYSICIAN_FIRSTNAME',
      'state_patientDemographics/SET_PHYSICIAN_MIDDLENAME',
    ]

    const validationErrors = Object.values(errors)
      .flat()
      .filter((error: IRuleError) => reduxActions.includes(error.id) && error.reduxStateOnlyFlag)

    if (validationErrors.length) {
      alert('Please Correct Errors')
      return
    }

    if (hasUnsavedPhysiciansData(patientDemographics)) {
      dispatch(addPhysicianData())
      dispatch(setPhysicianFirstName(''))
      dispatch(setPhysicianLastName(''))
      dispatch(setPhysicianMiddleName(''))
    }
    dispatch(savePatientDemographics())
  }

  useEffect(() => {
    if (alertMessage) {
      dispatch(showAlert({ message: alertMessage }))
    }
  }, [dispatch, alertMessage])

  useIonViewDidEnter(() => {
    if (!patientDemographics.__areDefaultsSet) {
      dispatch(setPatientDemographicsDefaults(true))
    }
  }, [patientDemographics.__areDefaultsSet])

  return (
    <IonPage id='PatientDemographicsPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' color='primary-alt' onClick={onBackClick}>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' color='primary-alt' fill='clear' onClick={saveOnClick}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('patientDemographics.PatientDemographics')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent id='main-pn-content' color='light' fullscreen>
        <PatientDemographicsContent />
      </IonContent>
    </IonPage>
  )
}

export default PatientDemographics
